import {
  faChevronDown,
  faChevronUp,
  faPenToSquare
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Flex, Table } from 'components';
import { ActiveDot } from 'components/ActiveDot/ActiveDot';
import { Button } from 'components/_form';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { IPriorityLevel } from 'types/priorityLevel';
import { PriorityLevelBadge } from '../PriorityLevelBadge/PriorityLevelBadge';

const Buttons = styled(Flex)`
  width: fit-content;
  margin: 0 auto;
  gap: 8px;
  align-items: center;
`;

const MoveButtons = styled(Flex)`
  flex-direction: column;
  gap: 2px;
`;

const CenteredCell = styled(Flex)`
  justify-content: center;
  align-items: center;
  width: 100%;
`;

interface Props {
  priorityLevels: IPriorityLevel[];
  onEdit: (priorityLevel: IPriorityLevel) => void;
  onChangeOrder: (
    priorityLevel: IPriorityLevel,
    type: 'increase' | 'decrease'
  ) => void;
}

export const PrioritiesTable: React.FC<Props> = ({
  priorityLevels,
  onEdit,
  onChangeOrder
}) => {
  const { t } = useTranslation();

  const sortedLevels = useMemo(
    () =>
      [...priorityLevels].sort(
        (a, b) => (a.order || 0) - (b.order || 0) || a.id - b.id
      ),
    [priorityLevels]
  );

  const centeredColumnKeys = [0];

  const header = [
    t('administrationTasksView.priorityTableHeaders.active'),
    t('administrationTasksView.priorityTableHeaders.priority'),
    t('administrationTasksView.priorityTableHeaders.usedInAreas'),
    ''
  ];

  const data = useMemo(
    () =>
      sortedLevels.map((priorityLevel) => ({
        active: (
          <CenteredCell key={`active-${priorityLevel.id}`}>
            <ActiveDot active={priorityLevel.active} />
          </CenteredCell>
        ),
        priority: (
          <PriorityLevelBadge
            key={`priority-${priorityLevel.id}`}
            priorityLevel={priorityLevel}
          />
        ),
        usedInAreas: (
          <span key={`areas-${priorityLevel.id}`}>
            {priorityLevel.areas?.map(({ name }) => name).filter(Boolean).join(', ') ||
              '—'}
          </span>
        ),
        actions: (
          <Buttons key={`actions-${priorityLevel.id}`}>
            <Button
              variant="eucalyptus"
              icon={<FontAwesomeIcon icon={faPenToSquare} />}
              onClick={() => onEdit(priorityLevel)}
            />
            <MoveButtons>
              <Button
                bordered
                transparent
                icon={<FontAwesomeIcon icon={faChevronUp} />}
                minHeight="17px"
                disabled={
                  sortedLevels.findIndex(({ id }) => id === priorityLevel.id) <=
                  0
                }
                onClick={() => onChangeOrder(priorityLevel, 'decrease')}
              />
              <Button
                bordered
                transparent
                icon={<FontAwesomeIcon icon={faChevronDown} />}
                minHeight="17px"
                disabled={
                  sortedLevels.findIndex(({ id }) => id === priorityLevel.id) >=
                  sortedLevels.length - 1
                }
                onClick={() => onChangeOrder(priorityLevel, 'increase')}
              />
            </MoveButtons>
          </Buttons>
        )
      })),
    [sortedLevels, onEdit, onChangeOrder]
  );

  return (
    <Table
      header={header}
      data={data}
      sortTable={false}
      leftFixedKeys={[1]}
      rightFixedKeys={[header.length - 1]}
      centeredColumnKeys={centeredColumnKeys}
    />
  );
};
